// Pure helpers for the onboarding reminder ladder (CWA-189).

import {
  EmailPreviewType,
  OnboardingReminders,
  ReminderOwner,
  ReminderRung,
  ReminderRungStatus,
} from "../types/onboarding";

type Severity = "success" | "info" | "warning" | "danger";

// The backend gives a rung up after this many failed attempts and moves on.
const MAX_ATTEMPTS = 3;

// The rung "Send next reminder now" sends: the first one not sent yet. A failed
// rung is retried, so it stays next until it has failed MAX_ATTEMPTS times.
export const nextRung = (reminders?: OnboardingReminders | null): ReminderRung | null =>
  reminders?.step
    ? reminders.rungs.find((r) => r.status !== "SENT" && !(r.status === "FAILED" && r.attempts >= MAX_ATTEMPTS)) ?? null
    : null;

// Only the two customer emails have a preview; the Slack rungs go to our own channel.
export const previewTypeFor = (rung?: ReminderRung | null): EmailPreviewType | null => {
  if (rung?.rung === "REMINDER_1") return "ONBOARDING_REMINDER_1";
  if (rung?.rung === "REMINDER_2") return "ONBOARDING_REMINDER_2";
  return null;
};

const STATUS: Record<ReminderRungStatus, [string, Severity]> = {
  SENT: ["Sent", "success"],
  FAILED: ["Failed", "danger"],
  PENDING: ["Sending", "warning"],
  DUE: ["Due", "warning"],
  SCHEDULED: ["Scheduled", "info"],
};

export const rungStatusLabel = (status: ReminderRungStatus): string => STATUS[status]?.[0] ?? status;
export const rungStatusSeverity = (status: ReminderRungStatus): Severity => STATUS[status]?.[1] ?? "info";

// The same words as the Prospects list's readiness column (CWA-228).
export const waitingOn = (owner: ReminderOwner): string =>
  owner === "CUSTOMER" ? "Waiting for the customer" : "Waiting for support";

export const addDays = (date: Date, days: number): Date => {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  copy.setDate(copy.getDate() + days);
  return copy;
};

// The local calendar date, YYYY-MM-DD. Not toISOString(): that is UTC and
// turns a late-evening pick into the previous day.
export const toIsoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// What the backend accepts (400 INVALID_SNOOZE otherwise): after today, at most 90 days ahead.
export const snoozeBounds = (today: Date = new Date()): { min: Date; max: Date } => ({
  min: addDays(today, 1),
  max: addDays(today, 90),
});

// "2026-11-05" → a readable local date, without shifting it through UTC.
export const formatIsoDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};
